import React, {useContext, useCallback, useState, useRef, useEffect} from "react";

import QuantitySelector from "@components/general/QuantitySelector.jsx"
import CartItem from "@components/general/CartItem.jsx"
import ProductOptions from "@components/general/ProductOptions.jsx"
import Button from "@components/general/Button.jsx";
import { CartContext } from "@contexts/Cart.jsx"
import { Engine } from "json-rules-engine"
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer'

import { getFormatedPrice, buildImageSrcSet } from "@utility/shopify"

export default ({item, product, editable=true, compatibilityKeys, hideSoldOut, includedItems, variant}) => {

  const [optionsOpen, setOptionsOpen] = useState(false)
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState(false);
  const [qty, setQty] = useState(null); //the loading state was not triggeing a re-render so this is a hack to force it to update
  const { addToCart, updateCart, cartNotes, cart } = useContext(CartContext);
  const rulesEngine = useRef(new Engine());


  let url, title, variant_title, quantity = 0, priceString, compareAtPriceString, imageSrc, imagAlt, imageSrcSet, variants = [];
  if(item){
    url = item.url;
    title = item.product_title;
    variant_title = item.variant_title;
    quantity = item.quantity;
    imageSrc = item.featured_image.url + "&width=2000";
    imagAlt = item.featured_image.alt;
    imageSrcSet = buildImageSrcSet(item.featured_image.url);
    priceString = getFormatedPrice(item.line_price);
    if(variant?.compareAtPrice?.amount && (variant?.compareAtPrice?.amount * item.quantity) > (item.line_price / 100)){
      compareAtPriceString = getFormatedPrice(variant?.compareAtPrice?.amount * item.quantity, 1);
    }
  }else if(product){
    url = "/products/" + product.handle;
    title = product.title;
    imageSrc = product.image.url + "&width=2000";
    imagAlt = product.image.alt;
    imageSrcSet = buildImageSrcSet(product.image.url);

    if(product.price?.maxVariantPrice?.amount == product.price?.minVariantPrice?.amount){
      priceString = getFormatedPrice(product.price.maxVariantPrice.amount, 1);
    }else{
      priceString = getFormatedPrice(product.price.minVariantPrice.amount, 1) + " - " + getFormatedPrice(product.price.maxVariantPrice.amount, 1);
    }

    if(product.price?.maxVariantPrice?.amount < product.compareAtPrice?.maxVariantPrice?.amount || product.price?.minVariantPrice?.amount < product.compareAtPrice?.minVariantPrice?.amount){
      if(product.compareAtPrice?.maxVariantPrice?.amount == product.compareAtPrice?.minVariantPrice?.amount){
        compareAtPriceString = getFormatedPrice(product.compareAtPrice.maxVariantPrice.amount, 1);
      }else{
        compareAtPriceString = getFormatedPrice(product.compareAtPrice.minVariantPrice.amount, 1) + " - " + getFormatedPrice(product.compareAtPrice.maxVariantPrice.amount, 1);
      }
    }

    if(product.variants.length == 1){
      variant_title = product.variants[0].title != "Default Title" && product.variants[0].title != product.title ? product.variants[0].title : null;
    }

    if(product.variants.length && hideSoldOut){
      for(var i = 0; i < product.variants.length; i++){
        const variant = product.variants[i];
        if(variant.available === false){
          return null;
        }
      }
    }

  }

  const addClick = useCallback(async () => {
    if(product?.variants.length > 1){
      setOptionsOpen(true);
    }else{
      if(product?.additionalOptions?.references.length > 0 && product?.additionalOptions?.references[0].variant){
        // add the first additional item if its a real item
        await addItem(product?.additionalOptions?.references[0].variant.reference);
      }
      addItem(product?.variants[0]);
    }
  }, [product])

  const addItem = useCallback(async (variant) => {
    setLoading(true);
    setOptionsOpen(false);
    await addToCart(variant);
        publishVariantAddEvent(variant, product);
    setLoading(false);
    setQty(1); // idk why this is needed but `loading` is not updating without it
  }, [setLoading]);

  const changeItem = useCallback(async (lineKey, targetQuantity) => {
    setLoading(true);
    await updateCart(lineKey, targetQuantity);
    if(item?.quantity < targetQuantity){
      publishVariantAddEvent(item, product);
    }
    setLoading(false);
    setQty(targetQuantity); // idk why this is needed but `loading` is not updating without it
  }, [setLoading]);

  let actions
  if(!optionsOpen && editable){
    actions =  item ? <QuantitySelector item={item} onChange={changeItem} busy={loading} /> : <Button busy={loading} onClick={addClick}>+ Add</Button>;
  }

  useEffect(() => {
    if(!cartNotes || !cartNotes.length) return;
    //         Cart Notes
    // ============================

    let filteredCartNotes = cartNotes;
    
    if(process.env.NODE_ENV != 'development' && !location.search.includes("debug")){
      filteredCartNotes = filteredCartNotes.filter((note) => note.active?.value == 'true');
    }

    filteredCartNotes?.forEach((note) => {
      try{
        rulesEngine.current.addRule({
          conditions: JSON.parse(note.rules?.value),
          event: {
            type: 'note',
            params: {
              content: note.content?.value,
              priority: note.priority?.value,
              level: note.level?.value
            }
          }
        });
      }catch(e){
        console.error(`Issue with cart note: ${note.name.value}`);
        console.error(e);
      }
    });

    const vehicle = VehicleData.getSelectedVehicle();

    const cart_skus = cart?.items.map((item) => item.sku);
    const cart_handles = cart?.items.map((item) => item.handle);

    let facts = {
        ...vehicle,
        option1: variant?.options[0]?.value || item?.options_with_values[0]?.value,
        option2: variant?.options[1]?.value || item?.options_with_values[1]?.value,
        option3: variant?.options[2]?.value || item?.options_with_values[2]?.value,
        sku: variant?.sku || item?.sku,
        qty: item?.quantity || 0, // default to 0 if no item
        handle: variant?.handle || item?.handle,
        available: variant?.available || item ? true : false,
        cart_skus: cart_skus,
        cart_handles: cart_handles
      }
    
    if(location.search.includes("debug")){
      console.log("======== DEBUGGING CART NOTE RULES - CUSTOMIZER ========");
      console.log("Facts:", facts);
      console.log("Notes:", filteredCartNotes);
    }

    rulesEngine.current
    .run(facts)
    .then(({ events }) => {
      events.sort((a, b) => a.params.priority - b.params.priority);
      setEvents(events);
      setLoading(false);
    });

  }, [rulesEngine, variant, item, cartNotes, cart]);

  const cartNote = events.length ? {
    level: events[0].params.level,
    content: convertSchemaToHtml(events[0].params.content) 
  } : null;

  return <>
    <CartItem url={url}
        properties={item?.properties}
        title={title}
        variant_title={variant_title}
        quantity={quantity}
        compareAtPriceString={compareAtPriceString}
        priceString={priceString}
        imageSrc={imageSrc}
        imagAlt={imagAlt}
        imageSrcSet={imageSrcSet}
        actions={actions}
        busy={loading}
        includedItems={includedItems}
        note={cartNote} />
    { product && product.variants.length > 1 && optionsOpen ? <ProductOptions productHandle={product.handle} variants={product.variants} onSelect={addItem} /> : null }
  </>
}


// publish variant:add event
const publishVariantAddEvent = (item, product) => {

  const variant = {
    product_type: product.type,
    product_title: product.title,
    image: typeof item.image === "string" ? item.image : item.image?.url,
    variant_id: item.id,
    price: typeof item.price === "number" ? item.price : item.price?.amount * 100,
    //item_brand: item.vendor,
    variant_title: item.title,
    sku: item.sku,
    quantity: 1,
    source: "customizer",
  }

  document.dispatchEvent(new CustomEvent("customizer:variant:add", {detail: {item: variant}}));
}